import { fireCmdbErrorAnalytics } from '@atlassian/jira-servicedesk-cmdb-error-handling/src/fireCmdbErrorAnalytics.tsx';
import type { InsightObjectAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import type { InsightObjectAttribute } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute.tsx';
import type { ObjectAttributeValueResponse } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-details.tsx';
import type { AttributeUpdateResponse } from '../../types/object-attribute.tsx';

export const assertDefined = <T,>(value: T): NonNullable<T> => {
	if (value != null) return value;

	fireCmdbErrorAnalytics({
		meta: {
			id: 'missing.property.from.insight.service',
			packageName: 'jiraServicedeskInsightObjectDetailsStore',
		},
	});
	throw new Error('Expected value from Insight service is null or undefined');
};

// used to normalize the attribute response from individual attribute updates
// rather than the attributes we get back as part of the object details response
export const normalizeObjectAttributeResponse = (
	attribute: InsightObjectAttribute,
	objectAttributeValues: ObjectAttributeValueResponse[],
): InsightObjectAttribute => {
	switch (attribute.type) {
		case 'default': {
			return {
				...attribute,
				defaultTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, group, project, user, status, ...attrValue } = attributeValue;
					return {
						...attrValue,
						value: attrValue.value,
					};
				}),
			};
		}

		case 'object':
			return {
				...attribute,
				objectTypeValues: objectAttributeValues.map((attributeValue) => {
					const { group, project, user, status, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						referencedObject: assertDefined(attrValue.referencedObject),
					};
				}),
			};

		case 'user':
			return {
				...attribute,
				userTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, group, project, status, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						user: assertDefined(attrValue.user),
					};
				}),
			};

		case 'group':
			return {
				...attribute,
				groupTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, project, user, status, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						group: assertDefined(attrValue.group),
					};
				}),
			};
		case 'project':
			return {
				...attribute,
				projectTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, user, group, status, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						project: assertDefined(attrValue.project),
					};
				}),
			};

		case 'status': {
			return {
				...attribute,
				statusTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, user, group, project, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						status: assertDefined(attrValue.status),
					};
				}),
			};
		}

		case 'bitbucketRepo': {
			return {
				...attribute,
				bitbucketRepoTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, user, group, project, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						bitbucketRepo: assertDefined(attrValue.bitbucketRepo),
					};
				}),
			};
		}

		case 'opsgenieTeam': {
			return {
				...attribute,
				opsgenieTeamTypeValues: objectAttributeValues.map((attributeValue) => {
					const { referencedObject, user, group, project, value, ...attrValue } = attributeValue;
					return {
						...attrValue,
						opsgenieTeam: assertDefined(attrValue.opsgenieTeam),
					};
				}),
			};
		}

		default: {
			// Because `attribute` will only be typed as 'never' when our switch
			// statement is exhaustive, we can use the following line as a way
			// to enforce it *before* runtime. So if there's a TS error on the
			// following line, it means we need to add a new case to the switch
			// statement to handle a new attribute.type.
			const assertSwitchIsExhaustive: never = attribute;
			fireCmdbErrorAnalytics({
				meta: {
					id: 'invalid.attribute.type',
					packageName: 'jiraServicedeskInsightObjectDetailsStore',
				},
			});
			throw new Error(
				`Invalid attribute type provided from Insight service found: ${JSON.stringify(
					assertSwitchIsExhaustive,
				)}`,
			);
		}
	}
};

export const getUpdatedAttributesAfterCreating = (
	attributeTypes: InsightObjectAttributeType[],
	attributes: InsightObjectAttribute[],
	attributeResponse: AttributeUpdateResponse,
) => {
	const { objectTypeAttributeId, objectAttributeValues } = attributeResponse;
	const attributeType = attributeTypes.find((at) => at.id === objectTypeAttributeId);
	if (!attributeType) {
		throw new Error('Expected attribute type to exist but none was found');
	}
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const partialAttribute: any = {
		objectTypeAttributeId,
		objectTypeAttribute: attributeType,
		type: attributeType.type,
	};
	const newAttribute = normalizeObjectAttributeResponse(partialAttribute, objectAttributeValues);
	return [...attributes, newAttribute] as const;
};
